/**
 * Public pricing page. Three plan columns with a monthly/yearly toggle; each Subscribe button
 * asks the server to open a hosted checkout for that plan and sends the browser there.
 *
 * Prices are strings the server already formatted from the plans table — the page does no price
 * math and never talks to the payment vendor directly. Card details are entered on the vendor's
 * hosted page, so nothing sensitive touches this app and there's no client billing SDK to load.
 *
 * It lives OUTSIDE the console's auth gate — a prospect who has never signed in can open it.
 */
import { useEffect, useMemo, useState } from 'react';
import { TIER_COPY, copyFor } from './tiers.ts';
import './pricing.css';

type Interval = 'month' | 'year';

interface ServerTier {
  code: string;
  name: string;
  priceMonth: string;
  priceYear: string;
  includedMinutes: number;
  overageCentsPerMinute: number;
  includedNumbers: number;
  maxBrands: number;
  purchasable: boolean;
}
interface BillingConfig {
  checkoutEnabled: boolean;
  tiers: ServerTier[];
}

export function PricingPage() {
  const [config, setConfig] = useState<BillingConfig | null>(null);
  const [interval, setInterval] = useState<Interval>('month');
  const [busy, setBusy] = useState<string | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const res = await fetch('/api/billing-config', { credentials: 'include' });
        if (!res.ok) throw new Error(`config ${res.status}`);
        const cfg = (await res.json()) as BillingConfig;
        if (live) setConfig(cfg);
      } catch {
        if (live) setFatal('Pricing is temporarily unavailable. Please try again shortly.');
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const tiers = useMemo(() => config?.tiers ?? [], [config]);

  const subscribe = async (t: ServerTier): Promise<void> => {
    setBusy(t.code);
    setNotice(null);
    try {
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: t.code, interval }),
      });
      const data = (await res.json()) as { ok?: boolean; url?: string; detail?: string };
      if (res.ok && data.url) {
        window.location.assign(data.url);
        return; // navigating away; leave the button in its busy state
      }
      setNotice(data.detail ?? 'Could not start checkout. Please try again.');
    } catch {
      setNotice('Could not reach checkout. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  if (fatal) {
    return (
      <main className="pricing">
        <div className="pricing-head">
          <h1>NightDesk pricing</h1>
          <p className="pricing-fatal">{fatal}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="pricing">
      <div className="pricing-head">
        <span className="pricing-mark" aria-hidden="true">
          N
        </span>
        <h1>Never miss an after-hours call again.</h1>
        <p className="pricing-sub">
          NightDesk answers when your office is closed, pages your on-call tech, and has the
          voicemail and a written summary waiting for you in the morning.
        </p>

        <div className="pricing-toggle" role="tablist" aria-label="Billing period">
          <button
            type="button"
            role="tab"
            aria-selected={interval === 'month'}
            className={interval === 'month' ? 'on' : ''}
            onClick={() => setInterval('month')}
          >
            Monthly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={interval === 'year'}
            className={interval === 'year' ? 'on' : ''}
            onClick={() => setInterval('year')}
          >
            Yearly
          </button>
        </div>
      </div>

      {notice && <p className="pricing-fatal">{notice}</p>}

      <div className="pricing-grid">
        {tiers.map((t) => {
          const copy = copyFor(t.code);
          const shown = interval === 'month' ? t.priceMonth : t.priceYear;
          return (
            <section key={t.code} className={`tier${copy?.highlight ? ' tier--hi' : ''}`}>
              {copy?.highlight && <span className="tier-flag">Most popular</span>}
              <h2 className="tier-name">{t.name}</h2>
              {copy?.tagline && <p className="tier-tagline">{copy.tagline}</p>}

              <div className="tier-price">
                <span className="tier-amount">{shown}</span>
                <span className="tier-per">/{interval === 'month' ? 'month' : 'year'}</span>
              </div>

              <button
                type="button"
                className={`tier-cta${copy?.highlight ? ' tier-cta--hi' : ''}`}
                disabled={busy !== null}
                onClick={() => void subscribe(t)}
              >
                {busy === t.code ? 'Starting…' : (copy?.cta ?? 'Subscribe')}
              </button>

              <ul className="tier-features">
                {(copy?.features ?? []).map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      <p className="pricing-foot">
        Overage minutes and extra numbers are billed only if you go past your plan. Applicable tax
        is added at checkout. Questions? <a href="mailto:hello@crescentlabs.ai">Talk to us.</a>
      </p>
    </main>
  );
}

export default PricingPage;
